import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@shared/api';
import { idr, num, relative } from '@shared/format';
import { EnvBadge, type ApiKeyRow } from '../components/ApiKeyBlocks';
import { ModelAccessSummary, useCatalogModels } from '../components/ModelAccess';
import { Alert, Badge, Card, Empty, Icon, InfoTip, Loading, PageHeader, Segmented, Tabs } from '../components/ui';
import {
  ActivityChart,
  BreakdownTable,
  PAGE_SIZE,
  Pager,
  RANGES,
  RequestDetailModal,
  RequestTable,
  UsageTiles,
  useGroupColors,
  type Activity,
  type Metric,
  type Range,
  type UsageRow,
} from '../components/UsageBlocks';
import { BudgetBlock, DAILY_TIP, FallbackChain, MONTHLY_TIP, ProjectModal, loadWeekSpend, type Project } from '../components/ProjectBlocks';
import '../styles/billing.css';

type Breakdown = 'model' | 'api_key';

const BREAKDOWNS: Array<{ id: Breakdown; label: string }> = [
  { id: 'model', label: 'Per model' },
  { id: 'api_key', label: 'Per API key' },
];

export function ProjectDetail() {
  const { id = '' } = useParams();
  const [project, setProject] = useState<Project | null | undefined>(undefined);
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [today, setToday] = useState<number | null>(null);
  const [range, setRange] = useState<Range>('30d');
  const [metric, setMetric] = useState<Metric>('spend_idr');
  const [breakdown, setBreakdown] = useState<Breakdown>('model');
  const [byModel, setByModel] = useState<Activity | null>(null);
  const [byKey, setByKey] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<UsageRow | null>(null);
  const [editing, setEditing] = useState(false);
  const catalog = useCatalogModels();
  const bySlug = useMemo(() => new Map((catalog ?? []).map((m) => [m.id, m])), [catalog]);

  const loadProject = useCallback(async () => {
    try {
      const [p, k, week] = await Promise.all([
        api<{ data: Project[] }>('/v1/projects'),
        api<{ data: ApiKeyRow[] }>(`/v1/api-keys?project_id=${id}`).catch(() => ({ data: [] as ApiKeyRow[] })),
        loadWeekSpend(),
      ]);
      setProject(p.data.find((item) => item.id === id) ?? null);
      setKeys(k.data);
      const series = week?.[id];
      setToday(week ? (series && series.length > 0 ? series[series.length - 1] : 0) : null);
    } catch (err) {
      setError((err as Error).message);
      setProject(null);
    }
  }, [id]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = (group: string) => `/v1/usage/activity?range=${range}&group_by=${group}&project_id=${id}`;
    Promise.all([api<Activity>(q('model')), api<Activity>(q('api_key')).catch(() => null)])
      .then(([model, key]) => {
        if (!alive) return;
        setByModel(model);
        setByKey(key);
        setError(null);
      })
      .catch((err: Error) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id, range]);

  useEffect(() => {
    if (!byModel) return;
    let alive = true;
    const query = new URLSearchParams({ project_id: id, from: byModel.from, to: byModel.to, page: String(page), page_size: String(PAGE_SIZE) });
    api<{ data: UsageRow[]; total: number }>(`/v1/usage?${query}`)
      .then((body) => {
        if (!alive) return;
        setRows(body.data);
        setTotal(body.total);
      })
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [byModel, id, page]);

  const modelColor = useGroupColors(byModel);
  const keyColor = useGroupColors(byKey);
  const spendByKey = useMemo(() => new Map((byKey?.groups ?? []).map((g) => [g.key, g.totals])), [byKey]);

  if (project === undefined) return <Loading label="Memuat proyek" />;
  if (project === null) {
    return (
      <Card>
        <Empty
          icon="folder"
          action={
            <Link className="btn" to="/projects">
              <Icon name="left" size={15} />
              Kembali ke Proyek
            </Link>
          }
        >
          {error ?? 'Proyek tidak ditemukan atau Anda tidak punya akses.'}
        </Empty>
      </Card>
    );
  }

  const periodLabel = RANGES.find((r) => r.id === range)?.label ?? '';
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const active = keys.filter((k) => k.status === 'ACTIVE');
  const current = breakdown === 'model' ? byModel : byKey;

  return (
    <>
      <Link to="/projects" className="bl-back">
        <Icon name="left" size={15} />
        Semua proyek
      </Link>
      <PageHeader
        title={
          <span className="bl-title">
            {project.name}
            <EnvBadge env={project.environment} />
            {project.status !== 'ACTIVE' && <Badge status={project.status}>Nonaktif</Badge>}
          </span>
        }
        subtitle={`${num(active.length)} key aktif${project.created_at ? ` · dibuat ${relative(project.created_at)}` : ''}. Statistik di halaman ini hanya dari proyek ini.`}
        actions={
          <>
            <Segmented label="Rentang" options={RANGES} value={range} onChange={(v) => { setRange(v); setPage(1); }} />
            <button className="btn" onClick={() => setEditing(true)}>
              <Icon name="gear" size={15} />
              Ubah
            </button>
          </>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      <Card className="bl-project mb">
        <div className="bl-project-body" style={{ borderTop: 0 }}>
          <BudgetBlock title="Harian" limitLabel="Batas harian" spentLabel="Terpakai hari ini" spent={today} limit={project.daily_budget_idr} tip={DAILY_TIP} />
          <BudgetBlock
            title="Bulanan"
            limitLabel="Batas bulanan"
            spentLabel="Terpakai bulan ini"
            spent={project.month_spend_idr}
            limit={project.monthly_budget_idr}
            tip={MONTHLY_TIP}
          />
          <div className="bl-budget">
            <div className="bl-budget-title">Model cadangan</div>
            {project.fallback_policy.length > 0 ? (
              <FallbackChain slugs={project.fallback_policy} models={bySlug} />
            ) : (
              <span className="muted small">Belum diatur. Bila provider gagal, error dikembalikan apa adanya.</span>
            )}
            <button className="btn btn-sm btn-ghost bl-inline-btn" onClick={() => setEditing(true)}>
              {project.fallback_policy.length > 0 ? 'Ubah urutan' : 'Atur model cadangan'}
            </button>
          </div>
        </div>
      </Card>

      {!byModel ? (
        loading ? <Loading label="Memuat statistik" /> : null
      ) : (
        <>
          <UsageTiles activity={byModel} periodLabel={periodLabel} />

          <ActivityChart activity={byModel} metric={metric} onMetric={setMetric} colorFor={modelColor} periodLabel={periodLabel} loading={loading} />

          <Card
            className="mb"
            title="Rincian biaya"
            description={breakdown === 'model' ? 'Model apa saja yang dipakai proyek ini.' : 'Key mana yang paling banyak memakai saldo.'}
            action={<Tabs tabs={BREAKDOWNS} value={breakdown} onChange={setBreakdown} tight />}
          >
            {current ? (
              <BreakdownTable
                activity={current}
                by={breakdown}
                label={breakdown === 'model' ? 'Model' : 'API key'}
                colorFor={breakdown === 'model' ? modelColor : keyColor}
              />
            ) : (
              <Empty>Rincian per key belum tersedia.</Empty>
            )}
          </Card>
        </>
      )}

      <Card
        className="mb"
        title="API key di proyek ini"
        description="Akses model dan biaya tiap key pada rentang yang dipilih."
        action={
          <Link className="btn btn-sm" to={`/api-keys?baru=1&proyek=${project.id}`}>
            <Icon name="plus" size={14} />
            Buat key
          </Link>
        }
      >
        {active.length === 0 ? (
          <Empty icon="key">Belum ada key aktif di proyek ini.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>
                    <span className="label-tip">
                      Model
                      <InfoTip label="Tentang akses model">Model yang boleh dipanggil key ini. Ubah lewat halaman API Key.</InfoTip>
                    </span>
                  </th>
                  <th>Terakhir dipakai</th>
                  <th className="num">Request</th>
                  <th className="num">Biaya {periodLabel}</th>
                </tr>
              </thead>
              <tbody>
                {active.map((key) => {
                  const spend = spendByKey.get(key.id);
                  return (
                    <tr key={key.id}>
                      <td style={{ minWidth: 160, maxWidth: 280 }}>
                        <div className="bl-stack">
                          <span className="bl-key-name truncate">{key.name}</span>
                          <span className="bl-id dim">
                            {key.key_prefix}
                            <span className="bl-mask">••••</span>
                          </span>
                        </div>
                      </td>
                      <td className="nowrap">
                        <ModelAccessSummary allowed={key.allowed_models} models={bySlug} />
                      </td>
                      <td className="nowrap">{key.last_used_at ? relative(key.last_used_at) : <span className="muted">Belum pernah</span>}</td>
                      <td className="num">{spend ? num(spend.requests) : <span className="muted">0</span>}</td>
                      <td className="num strong">{spend ? idr(spend.spend_idr) : <span className="muted">Rp0</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="field-hint mt-sm" style={{ marginBottom: 0 }}>
          Ubah nama, akses model, atau batas key di <Link to="/api-keys">halaman API Key</Link>.
        </p>
      </Card>

      <Card
        title="Request terbaru"
        description={`${num(total)} request dalam ${periodLabel} terakhir · klik baris untuk detail`}
        action={
          <div className="row wrap">
            <Link className="btn btn-sm btn-ghost" to={`/usage?proyek=${project.id}`}>
              Buka di Pemakaian
              <Icon name="arrow" size={14} />
            </Link>
            <Pager page={page} pages={pages} onPage={setPage} />
          </div>
        }
      >
        {!byModel ? (
          <Loading label="Memuat request" />
        ) : rows.length === 0 ? (
          <Empty
            action={
              <Link className="btn btn-sm" to="/playground">
                Coba di Playground
              </Link>
            }
          >
            Belum ada request dari proyek ini di rentang ini.
          </Empty>
        ) : (
          <RequestTable rows={rows} onSelect={setSelected} />
        )}
      </Card>

      {selected && <RequestDetailModal row={selected} onClose={() => setSelected(null)} />}

      {editing && (
        <ProjectModal
          project={project}
          models={catalog ?? []}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await loadProject();
          }}
        />
      )}
    </>
  );
}
